import React, { useMemo, useState } from 'react'
import { Autocomplete, Box, IconButton, InputBase, MenuItem, TextField, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Close as CloseIcon } from '@material-ui/icons'
import FileNameText from 'src/components/FileNameText'
import ResultButton from 'src/views/FileUploadView/components/ResultButton'
import { GENOTYPE_OPTIONS, MULTISAMPLE_RELATIONS } from 'src/services/analysisStart'
import { CELL_ROW_ATTR, ErrorLine, GroupLabel, Muted, Segmented, TextLink } from '../components/ui'
import { FinishedBrowser } from './FinishedList'
import FinishedPickerDialog from './FinishedPickerDialog'
import { addMembers, groupIssues, newGroup, removeMember, updateMember } from './model'

// The samples area of a Multisample analysis. Empty, it lists finished analyses to
// pick for the first group. Then one section per group (spacing only, no cards):
// its name, its members with relation, variants and genotype, and what keeps it
// from starting. Each group starts one analysis.

const SOFT_BUTTON_SX = { height: 32, px: 1.5, fontSize: 13.5 }
const GRID = 'minmax(0, 1.2fr) minmax(0, 1.1fr) 152px 150px 136px 32px'

const VARIANT_OPTIONS = [
  { value: 'include', label: 'Include' },
  { value: 'exclude', label: 'Exclude' },
]

const compactFieldSx = {
  width: '100%',
  '& .MuiInputBase-root': { fontSize: 13.5, height: 32, py: '0 !important' },
  '& .MuiInputBase-input': { py: '0 !important' },
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

const RelationCell = ({ member, error, onChange }) => (
  <Autocomplete
    freeSolo
    size="small"
    options={MULTISAMPLE_RELATIONS}
    value={member.relation || null}
    inputValue={member.relation || ''}
    onChange={(_e, value) => onChange(typeof value === 'string' ? value : '')}
    onInputChange={(_e, value) => onChange(value || '')}
    renderInput={(params) => (
      <TextField
        {...params}
        placeholder="Required"
        error={error}
        inputProps={{ ...params.inputProps, 'aria-label': `Relation of ${member.key}`, maxLength: 60 }}
        sx={compactFieldSx}
      />
    )}
  />
)

const MemberRow = ({ member, flagged, showErrors, onUpdate, onRemove }) => (
  <Box
    {...{ [CELL_ROW_ATTR]: '' }}
    sx={{ display: 'grid', gridTemplateColumns: GRID, columnGap: 1.5, alignItems: 'center', minHeight: 44 }}
  >
    <Box sx={{ minWidth: 0 }}>
      <FileNameText
        name={member.key}
        variant="plain"
        maxLines={1}
        sx={{ fontSize: 13.5, fontWeight: 500, color: showErrors && flagged ? 'error.main' : 'text.primary' }}
      />
    </Box>
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap title={member.summary}>
      {member.done === false ? 'Running' : member.summary}
    </Typography>
    <RelationCell
      member={member}
      error={showErrors && (!member.relation || !member.relation.trim())}
      onChange={(relation) => onUpdate({ relation })}
    />
    <Segmented
      ariaLabel={`Variants of ${member.key}`}
      value={member.include ? 'include' : 'exclude'}
      options={VARIANT_OPTIONS}
      onChange={(v) => onUpdate({ include: v === 'include' })}
      size="small"
    />
    <TextField
      select
      size="small"
      value={member.include ? member.gt || 'any' : 'any'}
      disabled={!member.include}
      onChange={(e) => onUpdate({ gt: e.target.value })}
      inputProps={{ 'aria-label': `Genotype of ${member.key}` }}
      sx={compactFieldSx}
    >
      {GENOTYPE_OPTIONS.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={{ fontSize: 14 }}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
    <IconButton size="small" aria-label={`Remove ${member.key}`} onClick={onRemove} sx={{ color: 'text.secondary' }}>
      <CloseIcon sx={{ fontSize: 18 }} />
    </IconButton>
  </Box>
)

const GroupSection = ({ group, index, showErrors, onChange, onRemove, onAddSamples }) => {
  const theme = useTheme()
  const issues = useMemo(() => groupIssues(group), [group])
  const flagged = new Set(issues.filter((i) => i.code !== 'no_relation').flatMap((i) => i.memberUids))
  const count = group.members.length
  return (
    <Box component="section" aria-label={group.name || `Group ${index + 1}`} sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <InputBase
          value={group.name}
          placeholder={`Group ${index + 1}`}
          onChange={(e) => onChange({ ...group, name: e.target.value })}
          inputProps={{ 'aria-label': `Name of group ${index + 1}`, maxLength: 120 }}
          sx={{
            fontSize: 16,
            fontWeight: 600,
            width: 260,
            maxWidth: '100%',
            borderRadius: `${theme.shape.borderRadius}px`,
            px: 0.75,
            mx: -0.75,
            '&:hover': { backgroundColor: 'action.hover' },
            '&.Mui-focused': { boxShadow: `0 0 0 1px ${theme.palette.primary.main}` },
            '& input::placeholder': { color: 'text.primary', opacity: 1 },
          }}
        />
        <Muted component="span">{plural(count, 'sample', 'samples')}</Muted>
        <Box sx={{ flex: 1 }} />
        <TextLink onClick={onAddSamples}>Add samples</TextLink>
        <TextLink tone="muted" onClick={onRemove}>
          Remove group
        </TextLink>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID,
          columnGap: 1.5,
          alignItems: 'end',
          pb: 0.5,
          '& > *': { fontSize: 12.5, color: 'text.secondary', fontWeight: 500 },
        }}
      >
        <Typography>Sample</Typography>
        <Typography>Analysis</Typography>
        <Typography>Relation</Typography>
        <Typography>Variants</Typography>
        <Typography>Genotype</Typography>
        <span />
      </Box>
      {group.members.map((member) => (
        <MemberRow
          key={member.uid}
          member={member}
          flagged={flagged.has(member.uid)}
          showErrors={showErrors}
          onUpdate={(patch) => onChange(updateMember(group, member.uid, patch))}
          onRemove={() => onChange(removeMember(group, member.uid))}
        />
      ))}
      {issues.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {issues.map((issue) =>
            showErrors ? (
              <ErrorLine key={issue.code} sx={{ mt: 0.25 }}>
                {issue.text}
              </ErrorLine>
            ) : (
              <Muted key={issue.code}>{issue.text}</Muted>
            ),
          )}
        </Box>
      )}
    </Box>
  )
}

const FirstGroupPicker = ({ onCreate }) => {
  const [selected, setSelected] = useState(() => new Map())
  const toggle = (row) =>
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(row.fileId)) next.delete(row.fileId)
      else next.set(row.fileId, row)
      return next
    })
  const selectMany = (rows) =>
    setSelected((prev) => {
      const next = new Map(prev)
      rows.forEach((row) => next.set(row.fileId, row))
      return next
    })
  const count = selected.size
  return (
    <Box sx={{ maxWidth: 880 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 1, mb: 1.5 }}>
        <GroupLabel sx={{ mb: 0 }}>Pick finished analyses for the first group</GroupLabel>
        <ResultButton disabled={!count} onClick={() => onCreate(Array.from(selected.values()))} sx={SOFT_BUTTON_SX}>
          {count ? `Add ${count} to a group` : 'Add to a group'}
        </ResultButton>
      </Box>
      <FinishedBrowser selected={selected} onToggle={toggle} onSelectMany={selectMany} />
    </Box>
  )
}

/**
 * @param groups    groupState.multisample.groups
 * @param onChange  (groups) => void
 */
const MultisampleArea = ({ groups, onChange, showErrors }) => {
  // null, or { target: group uid | 'new' }
  const [picker, setPicker] = useState(null)

  if (!groups.length) {
    return <FirstGroupPicker onCreate={(rows) => rows.length && onChange([newGroup(rows)])} />
  }

  const target = picker && picker.target !== 'new' ? groups.find((g) => g.uid === picker.target) : null
  const taken = target ? new Set(target.members.map((m) => String(m.fileId))) : undefined

  const addToPicked = (rows) => {
    if (!picker) return
    if (picker.target === 'new') onChange([...groups, newGroup(rows)])
    else onChange(groups.map((g) => (g.uid === picker.target ? addMembers(g, rows) : g)))
    setPicker(null)
  }

  return (
    <Box sx={{ maxWidth: 960 }}>
      {groups.map((group, index) => (
        <GroupSection
          key={group.uid}
          group={group}
          index={index}
          showErrors={showErrors}
          onChange={(next) => onChange(groups.map((g) => (g.uid === group.uid ? next : g)))}
          onRemove={() => onChange(groups.filter((g) => g.uid !== group.uid))}
          onAddSamples={() => setPicker({ target: group.uid })}
        />
      ))}
      <TextLink onClick={() => setPicker({ target: 'new' })}>Add a group</TextLink>
      <FinishedPickerDialog
        open={Boolean(picker)}
        onClose={() => setPicker(null)}
        title={
          picker?.target === 'new'
            ? `Group ${groups.length + 1}`
            : target
            ? `Add to ${target.name.trim() || `Group ${groups.indexOf(target) + 1}`}`
            : 'Add finished analyses'
        }
        taken={taken}
        newGroup={picker?.target === 'new'}
        onAdd={addToPicked}
      />
    </Box>
  )
}

export default MultisampleArea
